import React from 'react';
import { Box, Skeleton } from '@ublend-npm/aulaui-core';

interface MaterialListSkeletonProps {
  height?: number;
  width?: number;
  count?: number;
  kind?: 'rounded' | 'text' | 'circular';
  sx?: any;
}

export const MaterialListSkeleton = ({
  height,
  width,
  count = 4,
  kind = 'rounded',
  sx,
}: MaterialListSkeletonProps) => {
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <Skeleton sx={sx} kind={kind} key={i} height={height} width={width} />
      ))}
    </>
  );
};

// Styles for the version view skeleton
const skeletonContainerStyle = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'end',
  mt: '0.75rem',
};
const skeletonTitleWrapperStyle = {
  mt: '2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
};
const skeletonTitleStyle = { width: '100%', height: '25px' };
const skeletonViewWrapperStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};
const materialListSkeletonStyle = { width: '100%', height: '36px' };
const skeletonDate = { width: '30%', height: '25px', mb: '2rem' };

export const MaterialViewSkeleton = ({
  sx,
  showTitle = true,
  showButtons = true,
  count = 4,
}: {
  sx?: any;
  showTitle?: boolean;
  showButtons?: boolean;
  count?: number;
}) => {
  return (
    <Box sx={sx}>
      {showButtons && (
        <Box sx={skeletonContainerStyle}>
          <Skeleton
            id="button-skeleton"
            kind="rounded"
            height={36}
            width={180}
          />
        </Box>
      )}
      <Box id="version-title-skeleton" sx={skeletonTitleWrapperStyle}>
        {showTitle && (
          <>
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} sx={skeletonTitleStyle} kind="rounded" />
            ))}
            <Skeleton kind="rounded" sx={skeletonDate} />
          </>
        )}
        <Box id="version-view-skeleton" sx={skeletonViewWrapperStyle}>
          <MaterialListSkeleton sx={materialListSkeletonStyle} count={count} />
        </Box>
      </Box>
    </Box>
  );
};

export const MaterialPageSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    }}
  >
    {/* Button skeletons: Publish now + Edit */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px',
        mb: '16px',
      }}
    >
      <Skeleton kind="rounded" height={36} width={110} />
      <Skeleton kind="rounded" height={36} width={58} />
      <Skeleton role="status" kind="circular" width={36} height={36} />
    </Box>

    {/* Title section skeleton */}
    <Box
      sx={{
        mb: '24px',
        mt: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Skeleton kind="rounded" height={36} width="100%" />
      <Skeleton kind="rounded" height={36} width="30%" />
    </Box>

    {/* Content block skeletons */}
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '16px' }}
    >
      <Skeleton kind="rounded" height={36} width="100%" />
      <Skeleton kind="rounded" height={36} width="100%" />
      <Skeleton kind="rounded" height={36} width="100%" />
      <Skeleton kind="rounded" height={36} width="100%" />
    </Box>
  </Box>
);

export const MaterialSidebarSkeleton = () => (
  <>
    {Array.from({ length: 6 }, (_, i) => (
      <Box key={i} sx={{ mb: '12px' }}>
        <Skeleton
          kind="rounded"
          height={66}
          sx={{ width: '100%', minWidth: 0 }}
        />
      </Box>
    ))}
  </>
);

export const MOBILE_VERSION_LIST_ROUTE = '/version/list';
export const MOBILE_VERSION_VIEW_ROUTE = '/version/view';
export const MOBILE_VERSION_LIST_REGEX = /\/version\/list/;
export const MOBILE_VERSION_VIEW_REGEX = /\/version\/view/;
export const MOBILE_VERSION_ROUTE_REGEX = /\/version\/(list|view)/;
export const MATERIAL_ID_REGEX = /\/materials\/([^/]+)\/version/;
